// Estado da integração NPM. A fonte da verdade é o backend
// (GET /servers/{id}/npm/configuration → state/last_error/last_sync_*),
// persistido no audit log — um reload nunca mascara uma falha anterior.

const SYNC_MESSAGES = {
  auth_failed: "autenticação rejeitada pelo NPM",
  not_found: "API incompatível ou endpoint não encontrado",
  rate_limited: "limite de requisições do NPM",
  unavailable: "NPM indisponível",
  timeout: "timeout no NPM",
  invalid_request: "requisição rejeitada pelo NPM",
  resource_conflict: "recurso remoto não é gerenciado pela TintiCloud",
  acme_failed: "Let's Encrypt não emitiu o certificado",
  unknown: "resposta inválida do NPM",
};

// Configurado = credencial armazenada; Conectado = última verificação OK.
const CONNECTION_STATES = {
  not_configured: { label: "Não configurado", tone: "text-muted-foreground" },
  disabled: { label: "Desabilitado", tone: "text-muted-foreground" },
  unverified: { label: "Não verificado", tone: "text-warn" },
  connected: { label: "Conectado", tone: "text-ok" },
  auth_error: { label: "Erro de autenticação", tone: "text-err" },
  communication_error: { label: "Erro de comunicação", tone: "text-err" },
  error: { label: "Erro", tone: "text-err" },
};

export function npmConnectionState(config) {
  return CONNECTION_STATES[config?.state] || CONNECTION_STATES.unverified;
}

export function npmSyncFailure(error) {
  const detail = error?.detail && typeof error.detail === "object" ? error.detail : {};
  const code = SYNC_MESSAGES[detail.code] ? detail.code : "unknown";
  return {
    status: "failed",
    code,
    stage: detail.stage || null,
    http_status: detail.status_code || error?.status || null,
    message: SYNC_MESSAGES[code],
  };
}

export function npmSyncMessage(state) {
  return state?.message || SYNC_MESSAGES[state?.code] || SYNC_MESSAGES.unknown;
}

// Descrição de um erro persistido ({code, stage, status_code}) sem nenhum dado sensível.
export function npmErrorSummary(error) {
  if (!error) return null;
  const parts = [SYNC_MESSAGES[error.code] || SYNC_MESSAGES.unknown];
  if (error.stage) parts.push(`estágio ${error.stage}`);
  if (error.status_code) parts.push(`HTTP ${error.status_code}`);
  return parts.join(" · ");
}

// Mensagem legível de um ApiError de mutação (detail string, {message} ou erros 422).
export function npmApiErrorMessage(error) {
  const detail = error?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (detail && typeof detail === "object" && detail.message) {
    return detail.npm_message ? `${detail.message} (NPM: ${detail.npm_message})` : detail.message;
  }
  return error?.message || "Falha ao chamar o NPM";
}

// Payload de credenciais: senha vazia = manter a armazenada (edição).
export function npmCredentialsPayload({ base_url, identity, secret }) {
  const body = { base_url: (base_url || "").trim(), identity: (identity || "").trim() || null, enabled: true };
  if (secret) body.secret = secret;
  return body;
}

export const NPM_MANAGED_STATES = ["managed", "drifted"];

// Opções SSL coerentes com o NPM: sem Force SSL não há HSTS; sem HSTS não há subdomínios.
export function npmSslOptions({ force_ssl, http2_support, hsts_enabled, hsts_subdomains }) {
  const hsts = !!force_ssl && !!hsts_enabled;
  return { force_ssl: !!force_ssl, http2_support: !!http2_support, hsts_enabled: hsts, hsts_subdomains: hsts && !!hsts_subdomains };
}

export function npmSslLabel(row) {
  if (row.missing_since || row.certificate_id == null) return row.ssl_enabled == null ? "Unknown" : "Não";
  if (!row.certificate_id) return "Não";
  const flags = [row.force_ssl && "Force SSL", row.http2_support && "HTTP/2", row.hsts_enabled && (row.hsts_subdomains ? "HSTS+sub" : "HSTS")].filter(Boolean);
  const expires = row.certificate_expires_at ? ` · expira ${String(row.certificate_expires_at).slice(0, 10)}` : "";
  return `Certificado #${row.certificate_id}${expires}${flags.length ? ` · ${flags.join(", ")}` : ""}`;
}
